import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import logger from '@/services/logger';

class AuthService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.tokenKey = 'jwt_token';
    this.attributionKey = 'paperdoc_first_touch';
    this.deviceKey = 'paperdoc_device_fingerprint';
    this.validationTtlMs = 5 * 60 * 1000;
    this.validationCache = null;
    this.validationPromise = null;
  }

  /**
   * Get the stored JWT token
   */
  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Store JWT token
   */
  setToken(token) {
    localStorage.setItem(this.tokenKey, token);
    this.validationCache = { token, valid: true, checkedAt: Date.now() };
  }

  /**
   * Remove JWT token
   */
  removeToken() {
    localStorage.removeItem(this.tokenKey);
    this.validationCache = null;
    this.validationPromise = null;
  }

  /**
   * Check if user has a token (basic check)
   */
  hasToken() {
    return !!this.getToken();
  }

  /**
   * Decode JWT token to check expiration
   */
  isTokenExpired() {
    const token = this.getToken();
    if (!token) return true;

    try {
      // JWT tokens have 3 parts separated by dots
      const parts = token.split('.');
      if (parts.length !== 3) return true;

      // Decode the payload (second part)
      const raw = parts[1];
      const encoded = raw.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(raw.length / 4) * 4, '=');
      const payload = JSON.parse(atob(encoded));
      
      // Check if token has expiration time
      if (!payload.exp) return true;

      // Check if current time is past expiration (exp is in seconds, Date.now() is in milliseconds)
      const currentTime = Math.floor(Date.now() / 1000);
      return currentTime >= payload.exp;
    } catch (error) {
      logger.error('auth', 'Error decoding token:', error);
      return true; // If we can't decode it, consider it expired
    }
  }

  /**
   * Check if user is authenticated (has valid, non-expired token)
   */
  isAuthenticated() {
    return this.hasToken() && !this.isTokenExpired();
  }

  /**
   * Validate token with server
   */
  async validateToken({ force = false } = {}) {
    const token = this.getToken();
    if (!token || this.isTokenExpired()) return false;
    if (!force && this.validationCache?.token === token
      && Date.now() - this.validationCache.checkedAt < this.validationTtlMs) {
      return this.validationCache.valid;
    }
    if (this.validationPromise) return this.validationPromise;

    this.validationPromise = axios.get(`${this.baseURL}/api/user/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then((response) => {
      const valid = response.status === 200;
      this.validationCache = { token, valid, checkedAt: Date.now() };
      return valid;
    }).catch((error) => {
      logger.error('auth', 'Token validation failed:', error);
      if (error.response?.status === 401) {
        this.removeToken();
        return false;
      }
      // A temporary backend/network failure must not turn a valid local
      // session into a logout loop. Protected APIs remain authoritative.
      return true;
    }).finally(() => {
      this.validationPromise = null;
    });
    return this.validationPromise;
  }

  /**
   * Login user
   */
  async login(email, password) {
    let lastError = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await axios.post(`${this.baseURL}/api/login`, { email, password });
        const token = response.data.token;
        if (!token) throw new Error('No token received from server');
        this.setToken(token);
        return { success: true, user: response.data.user, token };
      } catch (error) {
        lastError = error;
        const status = error.response?.status;
        logger.error('auth', 'Email login attempt failed', {
          apiBaseUrl: this.baseURL,
          status: status || null,
          code: error.code || null,
          message: error.message || null,
        });
        if (status && status < 500) break;
        if (attempt === 0) await new Promise(resolve => window.setTimeout(resolve, 250));
      }
    }
    const serverMessage = lastError?.response?.data?.message;
    const networkMessage = process.env.NODE_ENV === 'development' && !lastError?.response
      ? `无法连接后端 ${this.baseURL}（${lastError?.code || lastError?.message || 'network error'}）`
      : null;
    return {
      success: false,
      error: serverMessage || networkMessage || '登录失败，请检查邮箱或密码。'
    };
  }

  captureAttribution(query = {}) {
    const existing = this.getAttribution();
    if (existing) return existing;
    const attribution = {};
    ['agent', 'campus', 'city', 'invite', 'campaign'].forEach((key) => {
      const value = Array.isArray(query[key]) ? query[key][0] : query[key];
      if (typeof value === 'string' && value.trim()) attribution[key] = value.trim().slice(0, 64);
    });
    if (!Object.keys(attribution).length) return null;
    const record = { attribution, expires_at: Date.now() + 30 * 24 * 60 * 60 * 1000 };
    localStorage.setItem(this.attributionKey, JSON.stringify(record));
    return attribution;
  }

  getAttribution() {
    try {
      const record = JSON.parse(localStorage.getItem(this.attributionKey) || 'null');
      if (!record?.attribution || Number(record.expires_at || 0) <= Date.now()) {
        localStorage.removeItem(this.attributionKey);
        return null;
      }
      return record.attribution;
    } catch (_) {
      localStorage.removeItem(this.attributionKey);
      return null;
    }
  }

  clearAttribution() { localStorage.removeItem(this.attributionKey); }

  getDeviceFingerprint() {
    let value = localStorage.getItem(this.deviceKey);
    if (!value) {
      value = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(this.deviceKey, value);
    }
    return value;
  }

  // Rejects on failure so the shared SMS button can show the server's message.
  // purpose: 'login' | 'register' | 'bind'
  async requestSms(phone, purpose) {
    const response = await axios.post(`${this.baseURL}/api/auth/sms`, { phone, purpose });
    return response.data;
  }

  async startPhoneSession(path, payload, fallback) {
    try {
      const response = await axios.post(`${this.baseURL}${path}`, payload);
      this.setToken(response.data.token);
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || fallback };
    }
  }

  phoneLogin(phone, code) {
    return this.startPhoneSession('/api/auth/login', { phone, code }, '登录失败，请稍后重试。');
  }

  phonePasswordLogin(phone, password) {
    return this.startPhoneSession('/api/auth/login/password', { phone, password }, '登录失败，请稍后重试。');
  }

  async phoneRegister({ username, phone, code, password, referralCode }) {
    const result = await this.startPhoneSession('/api/auth/register', {
      username, phone, code, password, referral_code: referralCode || undefined,
      terms_accepted: true, terms_version: '2026-08-08',
      attribution: this.getAttribution(), device_fingerprint: this.getDeviceFingerprint(),
    }, '注册失败，请稍后重试。');
    if (result.success) this.clearAttribution();
    return result;
  }

  /**
   * Logout user
   */
  async logout() {
    const token = this.getToken();
    
    // Call logout endpoint if token exists
    if (token) {
      try {
        await axios.post(`${this.baseURL}/api/user/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        logger.error('auth', 'Logout API error:', error);
        // Continue with logout even if API call fails
      }
    }

    // Remove token from storage
    this.removeToken();
  }

  /**
   * Get user profile information
   */
  async getUserProfile() {
    const token = this.getToken();
    if (!token) return null;

    try {
      const response = await axios.get(`${this.baseURL}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      logger.error('auth', 'Error fetching user profile:', error);
      if (error.response?.status === 401) {
        this.removeToken();
      }
      return null;
    }
  }

  /**
   * Check if current route should show authentication UI
   */
  shouldShowAuthUI(routePath) {
    const authRoutes = ['/login', '/auth', '/register'];
    return authRoutes.includes(routePath);
  }

  /**
   * Check if current route requires authentication
   */
  requiresAuth(routePath) {
    const publicRoutes = ['/', '/login', '/auth', '/register'];
    if (routePath.startsWith('/legal/')) return false;
    if (routePath.startsWith('/referral') || routePath.startsWith('/hq')) return false;
    if (routePath.startsWith('/p/') || routePath.startsWith('/task/')) return false;
    return !publicRoutes.includes(routePath);
  }

  /**
   * Get user's subscription status
   */
  async getSubscriptionStatus() {
    const token = this.getToken();
    if (!token) return null;

    try {
      const response = await axios.get(`${this.baseURL}/api/subscription/access-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      logger.error('auth', 'Error fetching subscription status:', error);
      if (error.response?.status === 401) {
        this.removeToken();
      }
      return null;
    }
  }

  /**
   * Check if user has an active subscription
   */
  async hasActiveSubscription() {
    const status = await this.getSubscriptionStatus();
    return status?.has_active_subscription || false;
  }

  /**
   * Handle 402 Payment Required errors intelligently
   */
  async handle402Error(error) {
    if (!error.response || error.response.status !== 402) {
      return false; // Not a 402 error, don't handle
    }

    const errorData = error.response.data;
    const errorCode = errorData?.code;

    logger.log('auth', 'Handling 402 error:', errorCode, errorData);
    logger.log('auth', 'Credits required - redirecting to journal package page');
    return '/p/journal';
  }
}

// Export singleton instance
export default new AuthService();
